import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { GET_CONSULT_NOTE_TEMPLATES } from '../../contracts';
import { getConsultNoteTemplates } from '../api/templates';
import { getTemplatesDataConfig } from '../config';

/**
 * Module-scope invalidator — runs outside a hook, so it takes the app's
 * queryClient singleton from the `configureTemplatesData` seam.
 */
export const invalidateNoteTemplates = () =>
  getTemplatesDataConfig().queryClient.invalidateQueries({
    queryKey: [GET_CONSULT_NOTE_TEMPLATES],
  });

type ConsultNoteTemplates = Awaited<
  ReturnType<typeof getConsultNoteTemplates>
>['data']['templates'];

function getCachedConsultNoteTemplates(): ConsultNoteTemplates | undefined {
  return getTemplatesDataConfig().queryClient.getQueryData<ConsultNoteTemplates>([
    GET_CONSULT_NOTE_TEMPLATES,
  ]);
}

/**
 * The consult-note template list (D2.7, ARCFE-1046).
 *
 * `enabled` is the app's `useIsDesktopOnlineReady()` read, composed app-side
 * (cohort-C). `onRequestError` is the severed toast: the app raises it at the
 * SAME position in the catch, so the offline branch still returns before it.
 *
 * The sort comparator, the offline cache fallback, and the deliberate rethrow
 * when there is nothing cached are all data behaviour and moved verbatim.
 */
export function useQueryConsultNoteTemplates(
  enabled: boolean,
  onRequestError: (error: AxiosError) => void
) {
  return useQuery({
    enabled,
    queryKey: [GET_CONSULT_NOTE_TEMPLATES],
    queryFn: async () => {
      try {
        const { data } = await getConsultNoteTemplates();
        return data.templates.sort((a, b) => {
          if (!a.created_time) return 1;
          if (!b.created_time) return -1;
          if (a.created_time && b.created_time) {
            return a.created_time < b.created_time ? 1 : -1;
          }
          return 0;
        });
      } catch (error) {
        if (error instanceof AxiosError) {
          if (getTemplatesDataConfig().isOfflineNetworkError(error)) {
            const cached = getCachedConsultNoteTemplates();
            if (cached) {
              return cached;
            }
            // No cache to fall back on — rethrow so the query enters an error
            // state and refetches on reconnect, rather than caching an empty
            // success that the global staleTime would treat as fresh.
            throw error;
          }

          onRequestError(error);
        }
      }
    },
  });
}
